import type { IRoleItem } from 'src/types/categories';
import type { IInspectorItem } from 'src/types/inspector';

import useSWR from 'swr';
import { useMemo } from 'react';

import axios, { fetcher, endpoints } from 'src/utils/axios';

import { DEFAULT_SWR_OPTIONS } from 'src/config/swr';

import { useAuthContext } from 'src/auth/hooks';

/**
 * Response structure for inspector list endpoints
 */
type InspectorData = {
  /** Array of inspector items */
  inspectors: IInspectorItem[];
};

const SWR_OPTIONS = {
  ...DEFAULT_SWR_OPTIONS,
  revalidateOnFocus: false,
};

export function useGetInspectorsForSpecificClient() {
  const { user } = useAuthContext();
  const url = `${endpoints.inspectors.list}?clientId=${user?.client?.id}`;
  const { data, isLoading, error, isValidating } = useSWR<InspectorData>(url, fetcher, SWR_OPTIONS);

  const memoizedValue = useMemo(
    () => ({
      data: data?.inspectors || [],
      loading: isLoading,
      error,
      isValidating,
      empty: !isLoading && !data?.inspectors,
    }),
    [data?.inspectors, error, isLoading, isValidating]
  );

  return memoizedValue;
}

export function useGetInspectorsByTeamId(teamId: number) {
  const url = typeof teamId === 'number' && !Number.isNaN(teamId)
    ? `${endpoints.inspectors.byTeamId}/${teamId}`
    : null;
  
  const { data, isLoading, error, isValidating } = useSWR<IInspectorItem[]>(
    url,
    fetcher, 
    DEFAULT_SWR_OPTIONS
  );

  const memoizedValue = useMemo(
    () => ({
      inspectors: data || [],
      inspectorsLoading: isLoading,
      inspectorsError: error,
      inspectorsValidating: isValidating,
      inspectorsEmpty: !isLoading && !data,
    }),
    [data, error, isLoading, isValidating]
  );

  return memoizedValue;
}

export function useGetUnassignedInspectors() {
  const url = endpoints.inspectors.unassigned;
  
  const { data, isLoading, error, isValidating } = useSWR<InspectorData>(url, fetcher, DEFAULT_SWR_OPTIONS);

  const memoizedValue = useMemo(
    () => ({
      inspectors: data?.inspectors || [],
      inspectorsLoading: isLoading,
      inspectorsError: error,
      inspectorsValidating: isValidating,
    }),
    [data?.inspectors, error, isLoading, isValidating]
  );

  return memoizedValue;
}

export function useGetMemberRoles() {
  const url = endpoints.memberRoles.list;

  const { data, isLoading, error, isValidating } = useSWR<IRoleItem[]>(url, fetcher, DEFAULT_SWR_OPTIONS);

  const memoizedValue = useMemo(
    () => ({
      roles: data || [],
      rolesLoading: isLoading,
      rolesError: error,
      rolesValidating: isValidating,
    }),
    [data, error, isLoading, isValidating]
  );

  return memoizedValue;
}

export function useGetInspectorsForFilterOptions() {
  const url = endpoints.inspectors.filterOptions;
  const { data, isLoading, error, isValidating } = useSWR<InspectorData>(url, fetcher, DEFAULT_SWR_OPTIONS);

  const memoizedValue = useMemo(
    () => ({
      inspectors: data?.inspectors || [],
      inspectorsLoading: isLoading,
      inspectorsError: error,
      inspectorsValidating: isValidating,
      inspectorsEmpty: !isLoading && !data?.inspectors,
    }),
    [data?.inspectors, error, isLoading, isValidating]
  );

  return memoizedValue;
}

export async function requestInspectorExport(filters: Record<string, unknown>): Promise<{
  exportId: number;
  status: string;
}> {
  const response = await axios.post<{ exportId: number; status: string }>(
    endpoints.inspectors.export,
    filters ?? {},
  );
  return response.data;
}

export async function downloadInspectorExport(
  exportId: number,
  fallbackFileName?: string,
): Promise<void> {
  const response = await axios.get(endpoints.inspectors.exportDownload(exportId), {
    responseType: 'blob',
  });

  const contentDisposition = response.headers['content-disposition'];
  let fileName = fallbackFileName || `inspectors_export_${exportId}.csv`;
  if (typeof contentDisposition === 'string') {
    const match = contentDisposition.match(/filename="?([^";\n]+)"?/);
    if (match?.[1]) {
      fileName = match[1];
    }
  }

  const blob = response.data as Blob;
  const objectUrl = window.URL.createObjectURL(blob);
  try {
    const anchor = document.createElement('a');
    anchor.href = objectUrl;
    anchor.download = fileName;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
  } finally {
    window.URL.revokeObjectURL(objectUrl);
  }
}
